import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { Modal } from '~/ui/modal/Modal';
import { Overlay } from '~/ui/overlay/Overlay';
// Removed Button import - using custom styled buttons
import NarrationPopup from '~/editor/narration/NarrationPopup';
import { CloseButton } from '~/ui/buttons/CloseButton';

const Title = styled.h2`
  margin: 0 0 24px 0;
  font-family: Raleway;
  font-weight: 600;
  font-size: 20px;
  color: #1a1a1a;
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-family: Raleway;
  font-weight: 500;
  font-size: 14px;
  color: #6b7280;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: Raleway;
  font-size: 14px;
  height: 44px;
  box-sizing: border-box;
  background: #ffffff;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #9845fb;
    box-shadow: 0 0 0 3px rgba(152, 69, 251, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const AddAudioButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  background: #f3f0ff;
  color: #9845fb;
  border: 1px solid #e0d4ff;
  border-radius: 8px;
  font-family: Raleway;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 20px;
  transition: all 0.2s ease;
  height: 44px;

  &:hover {
    background: #ede5ff;
    border-color: #d4c5ff;
  }

  &:active {
    transform: translateY(1px);
  }

  .icon {
    width: 16px;
    height: 16px;
  }
`;

const AudioPreview = styled.div`
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f8f1ff;
  border-radius: 8px;
  border: 1px solid #e0d4ff;

  .audio-info {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 8px;

    .name {
      font-family: Raleway;
      font-weight: 600;
      font-size: 14px;
      color: #333;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
`;

const RemoveAudioButton = styled.button`
  background: #f3ecff;
  border: none;
  color: #9738ff;
  cursor: pointer;
  font-size: 12px;
  padding: 12px 16px;
  border-radius: 4px;
  font-family: Raleway;
  font-weight: 600;
  transition: background 0.15s ease, color 0.15s ease;
  width: 100%;
  margin-bottom: 16px;
  display: block;

  &:hover {
    background: #9738ff;
    color: #fff;
  }

  &:active {
    transform: translateY(1px);
  }

  audio {
    width: 100%;
    height: 32px;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 32px;
`;

// Modal wrapper with closeBtn positioning - same as NarrationPopup
const ModalWrapper = styled.div`
  .closeBtn {
    margin-left: calc(100% - 70px);
  }
`;

const SubmitButton = styled.button`
  padding: 12px 60px;
  background: #9845fb;
  color: white;
  border: none;
  border-radius: 8px;
  font-family: Raleway;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;

  &:hover {
    background: #8532e8;
  }

  &:active {
    transform: translateY(1px);
  }

  &:disabled {
    background: #d1d5db;
    cursor: not-allowed;
    transform: none;
  }
`;

const MicrophoneIcon = () => (
  <svg className="icon" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C13.1 2 14 2.9 14 4V10C14 11.1 13.1 12 12 12C10.9 12 10 11.1 10 10V4C10 2.9 10.9 2 12 2ZM19 10V12C19 15.3 16.3 18 13 18V22H11V18C7.7 18 5 15.3 5 12V10H7V12C7 14.2 8.8 16 11 16H13C15.2 16 17 14.2 17 12V10H19Z" />
  </svg>
);

interface AudioItem {
  id: number;
  mediaUrl: string;
  originalFilename?: string;
  name?: string;
}

interface AddTextAudioModalProps {
  title: string;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    text: string;
    audio: AudioItem | null;
    coordinates: { x: number; y: number };
  }) => void;
  clickPosition: { x: number; y: number } | null;
  editingData?: {
    text: string;
    audio: AudioItem | null;
  };
}

const AddTextAudioModal: React.FC<AddTextAudioModalProps> = ({
  title = 'Add text and audio',
  isOpen,
  onClose,
  onSubmit,
  clickPosition,
  editingData
}) => {
  const [text, setText] = useState('');
  const [selectedAudio, setSelectedAudio] = useState<AudioItem | null>(null);
  const [showAudioSelector, setShowAudioSelector] = useState(false);

  // Initialize form with editing data when available
  React.useEffect(() => {
    if (editingData) {
      setText(editingData.text || '');
      if (editingData.audio) {
        setSelectedAudio(editingData.audio);
      }
    } else {
      setText('');
      setSelectedAudio(null);
    }
  }, [editingData, isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!text.trim()) {
      alert('Please enter some text');
      return;
    }

    if (!clickPosition) {
      alert('No position data available');
      return;
    }

    onSubmit({
      text: text.trim(),
      audio: selectedAudio,
      coordinates: clickPosition
    });

    handleClose();
  };

  const handleClose = () => {
    setText('');
    setSelectedAudio(null);
    setShowAudioSelector(false);
    onClose();
  };

  const handleAudioSelect = (audio: AudioItem) => {
    setSelectedAudio(audio);
    setShowAudioSelector(false);
  };

  const handleRemoveAudio = () => {
    setSelectedAudio(null);
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <>
      <Overlay open={isOpen} opacity={0.5} onClick={handleClose}>
        <div onClick={e => e.stopPropagation()}>
          <Modal
            open={true}
            width="400px"
            horizontal="center"
            vertical="center"
            padding="32px"
          >
            <ModalWrapper>
              <CloseButton
                type="close"
                onClick={handleClose}
                autoFocus={false}
              />
              <form onSubmit={handleSubmit}>
                <Title>{title}</Title>

                {/* Position info removed as requested */}

                <FormGroup>
                  <Label htmlFor="text">Text</Label>
                  <TextInput
                    id="text"
                    type="text"
                    value={text}
                    onChange={e => setText(e.target.value)}
                    placeholder="Enter your text here..."
                    required
                  />
                </FormGroup>

                {selectedAudio ? (
                  <>
                    <RemoveAudioButton
                      type="button"
                      onClick={handleRemoveAudio}
                      title="Remove audio"
                    >
                      Remove Audio
                    </RemoveAudioButton>
                    <AudioPreview>
                      <div className="audio-info"></div>
                      {selectedAudio.mediaUrl && (
                        <audio controls preload="none">
                          {selectedAudio.mediaUrl && (
                            <source src={selectedAudio.mediaUrl} />
                          )}
                        </audio>
                      )}
                    </AudioPreview>
                  </>
                ) : (
                  <AddAudioButton
                    type="button"
                    onClick={() => setShowAudioSelector(true)}
                  >
                    <MicrophoneIcon />
                    Add Audio
                  </AddAudioButton>
                )}

                <ButtonGroup>
                  <SubmitButton type="submit">Submit</SubmitButton>
                </ButtonGroup>
              </form>
            </ModalWrapper>
          </Modal>
        </div>
      </Overlay>

      {/* Audio Selector Modal */}
      {showAudioSelector && (
        <NarrationPopup
          onClose={() => setShowAudioSelector(false)}
          skipUpdate={true}
          onSelectCallback={handleAudioSelect}
          skipSequenceNarration={true}
        />
      )}
    </>,
    document.body
  );
};

export default AddTextAudioModal;
